import { constants, date } from '@eti/utils';
import type { Route } from '@eti/schema-types';
import { AgeType } from '@eti/schema-types';
import type { RebookEligibilityType } from '../order-details/components/manage-trip/rebooking/models';
import { RebookEligibility } from '../order-details/components/manage-trip/rebooking/models';
import type {
  RebookingPriceType,
  RebookingTravelerPricesType,
  RebookingTravelersType,
  RebookingTripType,
  TripSegmentsType,
  TripType,
} from './models';

type BoundInfoType = {
  arrivalCode: string;
  arrivedAt: string;
  departureCode: string;
  departuredAt: string;
  destination: string;
  marketingCarrierCode: string;
  marketingCarrierName: string;
  origin: string;
};

type CalculateRebookingPricesType = {
  airlineFees: number;
  airlinePenalty: number;
  fareDifference: number;
  serviceFee: number;
  taxDifference: number;
  totalDifference: number;
};

type RebookingTravelersPriceDetailsType = {
  ageType: AgeType;
  airlineFees: number;
  airlinePenalty: number;
  fareDifference: number;
  serviceFee: number;
  taxDifference: number;
  totalDifference: number;
};

type PriceBreakdownPerAgeTypeType = {
  ageType: string;
  airlineFees: number;
  airlinePenalty: number;
  fareDifference: number;
  quantity: number;
  serviceFee: number;
  taxDifference: number;
  totalDifference: number;
};

export const getBoundInfo = (segments: TripSegmentsType): BoundInfoType => {
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  let origin = '';
  let departuredAt = '';
  let departureCode = '';
  let marketingCarrierCode = '';
  let marketingCarrierName = '';
  if (firstSegment?.__typename === 'TripSegment') {
    origin = firstSegment.origin.cityName;
    departureCode = firstSegment.origin.code;
    departuredAt = firstSegment.departuredAt;
    marketingCarrierCode = firstSegment.marketingCarrier.code || '';
    marketingCarrierName = firstSegment.marketingCarrier.name || '';
  }

  let destination = '';
  let arrivedAt = '';
  let arrivalCode = '';
  if (lastSegment?.__typename === 'TripSegment') {
    destination = lastSegment.destination.cityName;
    arrivalCode = lastSegment.destination.code;
    arrivedAt = lastSegment.arrivedAt;
  }

  return {
    arrivalCode,
    arrivedAt,
    departureCode,
    departuredAt,
    destination,
    marketingCarrierCode,
    marketingCarrierName,
    origin,
  };
};

export const getTripRoutes = (
  newSearchDates: { departureDate: Date; returnDate?: Date | null },
  originalTrip: TripType,
): Route[] | null => {
  const { departureDate, returnDate } = newSearchDates;

  if (originalTrip.type === 'RETURN' && !returnDate) {
    return null;
  }

  return originalTrip.bounds.map((bound, index) => {
    const originSegment = bound.segments[0];
    const destinationSegment = bound.segments[bound.segments.length - 1];

    const originCode = originSegment?.__typename === 'TripSegment' ? originSegment.origin.code : '';
    const destinationCode =
      destinationSegment?.__typename === 'TripSegment' ? destinationSegment.destination.code : '';

    const departure = index === 0 ? departureDate : returnDate!!;

    return {
      departureAt: departure.getTime(),
      departureDate: date.format(departure, 'yyyy-MM-dd'),
      origin: originCode,
      destination: destinationCode,
    };
  });
};

export const formatGenesysRebookingTrip = (
  selectedTrip: RebookingTripType,
  dateFormat: string,
): string =>
  selectedTrip.bounds
    .map((bound) => {
      const originSegment = bound.segments[0];
      const destinationSegment = bound.segments[bound.segments?.length - 1];

      if (originSegment?.__typename !== 'TripSegment') {
        return '';
      }

      if (destinationSegment?.__typename !== 'TripSegment') {
        return '';
      }

      return `${originSegment.origin.code}-${destinationSegment.destination.code} ${date.format(
        originSegment.departuredAt,
        dateFormat,
      )}`;
    })
    .join(',');

export const calculateRebookingPrices = (
  rebookingPrice: RebookingPriceType,
  rebookEligibilityType?: RebookEligibilityType | null,
): CalculateRebookingPricesType => {
  const isFlexibleTicket =
    rebookEligibilityType === RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF;

  const airlinePenalty = isFlexibleTicket ? 0 : rebookingPrice.airlinePenalty?.value || 0;
  const serviceFee = isFlexibleTicket ? 0 : rebookingPrice.serviceFee?.value || 0;
  const totalDifference = isFlexibleTicket
    ? (rebookingPrice.totalDifference?.value || 0) -
      (rebookingPrice.airlinePenalty?.value || 0) -
      (rebookingPrice.serviceFee?.value || 0)
    : rebookingPrice.totalDifference?.value || 0;

  return {
    airlineFees: rebookingPrice.airlineFees?.value || 0,
    airlinePenalty,
    fareDifference: rebookingPrice.fareDifference?.value || 0,
    serviceFee,
    taxDifference: rebookingPrice.taxDifference?.value || 0,
    totalDifference,
  };
};

export const getTravelerPriceDetails = (
  travelers: RebookingTravelersType,
  travelerPrices: RebookingTravelerPricesType,
  rebookEligibilityType: RebookEligibilityType,
) => {
  return travelers.map((traveler) => {
    const travelerPrice = travelerPrices.find((price) => price.travelerId === traveler.id)
      ?.rebookingPrice;

    const priceDetails = travelerPrice
      ? calculateRebookingPrices(travelerPrice, rebookEligibilityType)
      : {
          fareDifference: 0,
          taxDifference: 0,
          airlinePenalty: 0,
          airlineFees: 0,
          serviceFee: 0,
          totalDifference: 0,
        };

    return {
      ageType: traveler.ageType,
      ...priceDetails,
    };
  });
};

export type TravelersByAgeType = {
  serviceFee: number;
  taxDifference: number;
  quantity: number;
  totalDifference: number;
  airlineFees: number;
  airlinePenalty: number;
  fareDifference: number;
  ageType: string;
};

export const groupTravelersByAgeType = (
  travelers: RebookingTravelersType,
  travelerPrices: RebookingTravelerPricesType,
  rebookEligibilityType: RebookEligibilityType,
): TravelersByAgeType[] => {
  const travelerDetails = getTravelerPriceDetails(travelers, travelerPrices, rebookEligibilityType);

  const groupedByAge: Record<AgeType, RebookingTravelersPriceDetailsType[]> =
    travelerDetails.reduce(
      (acc, traveler) => {
        const { ageType } = traveler;
        if (!acc[ageType]) {
          acc[ageType] = [];
        }
        acc[ageType].push(traveler);
        return acc;
      },
      {} as Record<AgeType, RebookingTravelersPriceDetailsType[]>,
    );

  return Object.entries(groupedByAge).map(([ageType, group]) => {
    const [firstTraveler] = group;
    return {
      ageType,
      airlineFees: firstTraveler.airlineFees,
      airlinePenalty: firstTraveler.airlinePenalty,
      fareDifference: firstTraveler.fareDifference,
      serviceFee: firstTraveler.serviceFee,
      taxDifference: firstTraveler.taxDifference,
      totalDifference: firstTraveler.totalDifference,
      quantity: group.length,
    };
  });
};

export const getTotalRebookingPrice = (groupedPrices: PriceBreakdownPerAgeTypeType[]): number =>
  groupedPrices.reduce(
    (total, { totalDifference, quantity }) => total + totalDifference * quantity,
    0,
  );

export const getTripDisplayRebookingPrice = (
  tripPriceDisplayType: string,
  groupedPrices: PriceBreakdownPerAgeTypeType[] = [],
) => {
  const { priceDisplayTypes } = constants;
  const totalRebookingPrice = getTotalRebookingPrice(groupedPrices);
  switch (tripPriceDisplayType) {
    case priceDisplayTypes.TOTAL:
      return totalRebookingPrice;
    case priceDisplayTypes.PER_TRAVELER: {
      const totalQuantity = groupedPrices.reduce((sum, { quantity }) => sum + quantity, 0);
      return totalQuantity ? totalRebookingPrice / totalQuantity : 0;
    }
    case priceDisplayTypes.PER_ADULT:
    default: {
      const adultGroup = groupedPrices.find((goup) => goup.ageType === AgeType.Adt);
      return adultGroup ? adultGroup.totalDifference : 0;
    }
  }
};
